/**
 * The "Compacted" sheet: the running summary the model reads in place of older
 * messages, split by the pass that wrote each part, and the history of every
 * compaction on the thread.  Reads stored state (`/threads/:id/summaries`), so
 * it survives reloads; live compaction events only trigger a refetch.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X } from 'lucide-react';
import { api } from '@/services/api';

export interface SummaryViewSegment {
  text: string;
  createdAt: string;
  coveredFrom: number;
  coveredTo: number;
  turnIndex?: number;
  source?: 'turn-start' | 'mid-turn';
  mode?: string;
  step?: number;
}

export interface SummaryHistoryEntry {
  turnIndex: number;
  timestamp: string;
  kind: 'turn-start' | 'mid-turn';
  mode?: string;
  step?: number;
  persisted?: boolean;
  beforeTokens?: number;
  afterTokens?: number;
  continuations?: number;
  partial?: boolean;
  turnCostUsd?: number;
}

export interface SummaryView {
  current: null | {
    stateText: string;
    segments: SummaryViewSegment[];
    references: string;
    messageCount: number;
    createdAt: string;
    partial: boolean;
    previous: null | { stateText: string; references: string; createdAt: string };
  };
  history: SummaryHistoryEntry[];
}

/** The stored summary view for a thread; refetched whenever `refreshKey` changes. */
export function useSummaryView(threadId: string | null, refreshKey: unknown): SummaryView | null {
  const [view, setView] = useState<SummaryView | null>(null);
  useEffect(() => {
    setView(null);
  }, [threadId]);
  useEffect(() => {
    if (!threadId) return;
    let live = true;
    api.get<SummaryView>(`/chat/threads/${threadId}/summaries`).then((v) => { if (live) setView(v); }).catch(() => {});
    return () => { live = false; };
  }, [threadId, refreshKey]);
  return view;
}

// ── Formatting ───────────────────────────────────────────────────────────────

const n = (x: number) => x.toLocaleString();
const k = (x: number) => `${Math.round(x / 1000)}K`;

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return d.toLocaleString(undefined, today
    ? { hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function modeWord(mode?: string): string {
  switch (mode) {
    case 'extend': return 'extended';
    case 'recompact': return 'rewritten';
    case 'full': return 'first summary';
    case 'salvage': return 'rebuilt';
    default: return '';
  }
}

/** What a segment is, in one line: who wrote it, when, and what it covers. */
function segmentCaption(seg: SummaryViewSegment, index: number): string {
  const turn = typeof seg.turnIndex === 'number' ? `turn ${seg.turnIndex}` : '';
  const at = seg.source === 'mid-turn' && typeof seg.step === 'number' ? `${turn}, mid-turn step ${seg.step}` : turn;
  const lead = index === 0
    ? seg.mode === 'recompact' ? 'Rewritten' : 'Base summary'
    : 'Added';
  const parts = [
    at ? `${lead}${index === 0 && lead === 'Base summary' ? ` · ${at}` : ` at ${at}`}` : lead,
    when(seg.createdAt),
    seg.coveredTo > seg.coveredFrom ? `messages ${n(seg.coveredFrom)}–${n(seg.coveredTo)}` : '',
  ];
  return parts.filter(Boolean).join(' · ');
}

const PROSE =
  'prose prose-sm prose-zinc max-w-none text-[13px] leading-relaxed text-zinc-700 ' +
  'prose-headings:text-[13px] prose-headings:font-semibold prose-headings:text-zinc-900 prose-headings:mt-3 prose-headings:mb-1 ' +
  'prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-li:marker:text-zinc-300 ' +
  'prose-code:text-[12px] prose-code:font-mono prose-code:text-zinc-800 prose-code:bg-zinc-100 prose-code:rounded prose-code:px-1 prose-code:before:content-none prose-code:after:content-none ' +
  'prose-strong:text-zinc-900 prose-strong:font-semibold';

/** The state block's own `## Current State` heading is redundant under our label. */
const stripStateHeading = (s: string) => s.replace(/^\s*##\s*Current State\s*\n/i, '');

// ── Sheet ────────────────────────────────────────────────────────────────────

export function SummarySheet({ view, onClose, live }: {
  view: SummaryView | null;
  onClose: () => void;
  /** The pass that just ran in this session, if any — for its token numbers. */
  live?: { scope?: string; beforeTokens?: number; afterTokens?: number; tokens?: number; durationMs?: number } | null;
}) {
  const [tab, setTab] = useState<'summary' | 'history'>('summary');
  const current = view?.current ?? null;
  const segments = current?.segments ?? [];
  const history = view?.history ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/20" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[86vh] flex flex-col rounded-lg bg-white shadow-lg border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3 border-b border-zinc-100">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-zinc-900">Context summary</h2>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">
              {current
                ? <>What the model reads in place of the first {n(current.messageCount)} messages. The transcript itself is unchanged.</>
                : <>This thread hasn't been compacted yet.</>}
            </p>
            <div className="mt-3 flex gap-1 text-xs">
              {(['summary', 'history'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`h-6 rounded-md px-2 transition-colors ${tab === t ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500 hover:text-zinc-800'}`}
                >
                  {t === 'summary' ? 'Summary' : `History${history.length ? ` · ${history.length}` : ''}`}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!view ? (
            <p className="text-xs text-zinc-400">Loading…</p>
          ) : tab === 'summary' ? (
            current ? (
              <div className="space-y-5">
                {current.partial && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    The last update stopped at its output limit before finishing. The next one rebuilds what's missing.
                  </p>
                )}

                {current.stateText && (
                  <section>
                    <SectionLabel
                      title="Current state"
                      note={current.previous ? 'rewritten on every update — what the latest one changed is marked' : 'rewritten on every update'}
                    />
                    <LineDiff
                      next={stripStateHeading(current.stateText)}
                      prev={current.previous ? stripStateHeading(current.previous.stateText) : null}
                    />
                  </section>
                )}

                <section>
                  <SectionLabel
                    title="Narrative"
                    note={segments.length > 1 ? `${segments.length} parts, oldest first — one per update; the newest is open` : 'written in one pass'}
                  />
                  <ol className="space-y-2">
                    {segments.map((seg, i) => (
                      <SegmentItem
                        key={`${seg.createdAt}-${i}`}
                        seg={seg}
                        index={i}
                        latest={segments.length > 1 && i === segments.length - 1}
                        only={segments.length === 1}
                      />
                    ))}
                  </ol>
                </section>

                {current.references && (
                  <details className="group">
                    <summary className="cursor-pointer list-none text-2xs uppercase tracking-wide text-zinc-400 hover:text-zinc-600">
                      References <span className="normal-case tracking-normal">— where the model can look things up</span>
                    </summary>
                    <div className="mt-2">
                      <LineDiff next={current.references} prev={current.previous ? current.previous.references : null} />
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Nothing to show yet. A summary appears once the conversation outgrows its context budget.</p>
            )
          ) : history.length === 0 ? (
            <p className="text-xs text-zinc-500">No compactions recorded for this thread.</p>
          ) : (
            <ol className="divide-y divide-zinc-100">
              {[...history].reverse().map((h, i) => (
                <li key={`${h.turnIndex}-${h.kind}-${h.step ?? ''}-${i}`} className="flex items-baseline justify-between gap-4 py-2 text-xs">
                  <div className="min-w-0">
                    <span className="text-zinc-900">Turn {h.turnIndex}</span>
                    <span className="text-zinc-500">
                      {' · '}
                      {h.kind === 'mid-turn' ? `mid-turn${typeof h.step === 'number' ? `, step ${h.step}` : ''}` : 'start of turn'}
                      {modeWord(h.mode) ? ` · ${modeWord(h.mode)}` : ''}
                      {h.kind === 'mid-turn' && h.persisted === false ? ' · that turn only' : ''}
                      {h.continuations ? ` · continued ${h.continuations}×` : ''}
                      {h.partial ? ' · cut off' : ''}
                    </span>
                  </div>
                  <div className="shrink-0 tabular-nums text-zinc-400">
                    {h.beforeTokens && h.afterTokens ? `${k(h.beforeTokens)} → ${k(h.afterTokens)} · ` : ''}
                    {when(h.timestamp)}
                    {h.turnCostUsd ? ` · $${h.turnCostUsd.toFixed(3)}` : ''}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {live && (live.beforeTokens || live.tokens || live.afterTokens) && (
          <div className="px-5 py-2.5 border-t border-zinc-100 text-xs text-zinc-400 tabular-nums">
            Last pass: {live.scope === 'mid-turn' ? 'mid-turn' : 'start of turn'}
            {` · ${k(live.beforeTokens ?? live.tokens ?? 0)} → ${live.afterTokens ? k(live.afterTokens) : '?'} tokens`}
            {typeof live.durationMs === 'number' ? ` · ${(live.durationMs / 1000).toFixed(1)}s` : ''}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** First line of real prose in a segment, for the collapsed view. */
function previewOf(text: string): string {
  const line = text
    .split('\n')
    .filter((l) => !/^\s*#/.test(l)) // headings ("Update — newer messages", section names) say nothing
    .map((l) => l.replace(/^[>*\-\d.\s]+/, '').replace(/[`*_]/g, '').trim())
    .find((l) => l.length > 20);
  if (!line) return '';
  return line.length > 140 ? `${line.slice(0, 140).trimEnd()}…` : line;
}

/**
 * One pass's part of the narrative.  The newest (or a lone) part is open; older
 * parts collapse to their caption and a one-line preview, so what the latest
 * update added stands out without scrolling past every earlier one.
 */
function SegmentItem({ seg, index, latest, only }: { seg: SummaryViewSegment; index: number; latest: boolean; only: boolean }) {
  const [open, setOpen] = useState(latest || only);
  const empty = !seg.text.trim();
  const preview = empty ? '' : previewOf(seg.text);
  return (
    <li className={`border-l-2 pl-3 ${latest ? 'border-brand-400 bg-brand-50/40 py-2 pr-2 rounded-r-md' : 'border-zinc-200'}`}>
      <button
        type="button"
        onClick={() => !empty && setOpen((o) => !o)}
        className={`flex w-full items-baseline justify-between gap-3 text-left text-2xs tabular-nums ${empty ? 'cursor-default' : 'cursor-pointer'} text-zinc-400 hover:text-zinc-600`}
      >
        <span>{segmentCaption(seg, index)}</span>
        <span className="shrink-0">
          {latest ? <span className="text-brand-600">Latest</span> : !empty && !only ? (open ? 'Hide' : 'Show') : null}
        </span>
      </button>
      {empty ? (
        <p className="mt-1 text-xs italic text-zinc-400">This update added nothing.</p>
      ) : open ? (
        <div className={`${PROSE} mt-1`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.text}</ReactMarkdown>
        </div>
      ) : (
        preview && <p className="mt-0.5 truncate text-xs text-zinc-500">{preview}</p>
      )}
    </li>
  );
}

// ── Line diff (state and references) ─────────────────────────────────────────

const norm = (l: string) => l.trim().replace(/\s+/g, ' ');

/** Which lines of `next` are new since `prev`, and which lines of `prev` are gone (LCS on trimmed lines). */
export function diffLines(prev: string, next: string): { lines: Array<{ text: string; added: boolean }>; removed: string[] } {
  const a = prev.split('\n').filter((l) => l.trim());
  const b = next.split('\n').filter((l) => l.trim());
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = norm(a[i]) === norm(b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const lines: Array<{ text: string; added: boolean }> = [];
  const removed: string[] = [];
  let i = 0, j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && norm(a[i]) === norm(b[j])) { lines.push({ text: b[j], added: false }); i++; j++; }
    else if (j < n && (i >= m || dp[i][j + 1] >= dp[i + 1][j])) { lines.push({ text: b[j], added: true }); j++; }
    else { removed.push(a[i]); i++; }
  }
  return { lines, removed };
}

/** Consecutive lines grouped by whether the latest update added them. */
function runs(lines: Array<{ text: string; added: boolean }>): Array<{ added: boolean; texts: string[] }> {
  const out: Array<{ added: boolean; texts: string[] }> = [];
  for (const l of lines) {
    const last = out[out.length - 1];
    if (last && last.added === l.added) last.texts.push(l.text);
    else out.push({ added: l.added, texts: [l.text] });
  }
  return out;
}

/** One markdown line, rendered inline: bullets get a dot, headings a small label. */
function MdLine({ text, className = '' }: { text: string; className?: string }) {
  const heading = /^\s*#{1,6}\s+/.test(text);
  const bullet = /^\s*([-*+]|\d+\.)\s+/.exec(text);
  const indent = Math.min(3, Math.floor((text.match(/^\s*/)?.[0].length ?? 0) / 2));
  const body = text.replace(/^\s*#{1,6}\s+/, '').replace(/^\s*([-*+]|\d+\.)\s+/, '');
  return (
    <div className={`flex gap-2 ${heading ? 'mt-2 font-semibold text-zinc-900' : ''} ${className}`} style={{ paddingLeft: indent * 14 }}>
      {bullet && <span className="select-none text-zinc-300">{/\d/.test(bullet[1]) ? bullet[1] : '•'}</span>}
      <div className={`min-w-0 ${PROSE} [&_p]:my-0`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
      </div>
    </div>
  );
}

/**
 * A block shown line by line, with the lines the latest update added marked
 * and the ones it dropped listed underneath.  Without a previous version it is
 * just the block.
 */
function LineDiff({ next, prev }: { next: string; prev: string | null }) {
  const { lines, removed } = prev == null
    ? { lines: next.split('\n').filter((l) => l.trim()).map((text) => ({ text, added: false })), removed: [] as string[] }
    : diffLines(prev, next);
  const changed = prev != null && (removed.length > 0 || lines.some((l) => l.added));
  return (
    <div className="space-y-0.5">
      {runs(lines).map((run, i) =>
        run.added ? (
          // A run of new lines reads as one marked block, not a stack of pills.
          <div key={i} className="-ml-3 space-y-0.5 rounded-r-md border-l-2 border-brand-400 bg-brand-50/50 py-1 pl-[10px] pr-1">
            {run.texts.map((t, j) => <MdLine key={j} text={t} />)}
          </div>
        ) : (
          run.texts.map((t, j) => <MdLine key={`${i}-${j}`} text={t} />)
        ),
      )}
      {prev != null && !changed && <p className="pt-1 text-2xs text-zinc-400">Unchanged by the latest update.</p>}
      {removed.length > 0 && (
        <div className="pt-2">
          <div className="mb-1 text-2xs text-zinc-400">Removed in the latest update</div>
          {removed.map((r, i) => (
            <MdLine key={i} text={r} className="text-zinc-400 line-through decoration-zinc-300 [&_*]:!text-zinc-400" />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ title, note }: { title: string; note?: string }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <h3 className="text-2xs font-medium uppercase tracking-wide text-zinc-500">{title}</h3>
      {note && <span className="text-2xs text-zinc-400">{note}</span>}
    </div>
  );
}
